import React, { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useApiRouteMonitor } from '../contexts/ApiRouteMonitor';
import { mockData } from '../mocks/mockData';
import { clearAuthData } from '../utils/clearAuth';
import { apiTestService, ApiTestResult } from '../services/apiTestService';

const AuthDebug: React.FC = () => {
  const { user, isAuthenticated, apiStatus } = useAuth();
  const { routeStatuses, clearRouteStatuses } = useApiRouteMonitor();
  const [isExpanded, setIsExpanded] = useState(false);
  const [showRouteMonitor, setShowRouteMonitor] = useState(false);
  const [showApiStatus, setShowApiStatus] = useState(false);
  const [apiTestResults, setApiTestResults] = useState<ApiTestResult[]>([]);
  const [isTestingApis, setIsTestingApis] = useState(false);

  // Testar APIs automaticamente quando o componente montar
  useEffect(() => {
    if (isAuthenticated) {
      testAllApis();
    }
  }, [isAuthenticated]);

  const testAllApis = async () => {
    setIsTestingApis(true);
    try {
      // Usar o método mais rápido inicialmente
      const results = await apiTestService.testMainApis();
      setApiTestResults(results);
    } catch (error) {
      console.error('Erro ao testar APIs:', error);
    } finally {
      setIsTestingApis(false);
    }
  };

  const testAllApisComplete = async () => {
    setIsTestingApis(true);
    try {
      // Testar todas as APIs
      const results = await apiTestService.testAllApis();
      setApiTestResults(results);
    } catch (error) {
      console.error('Erro ao testar APIs:', error);
    } finally {
      setIsTestingApis(false);
    }
  };

  const handleClearAuth = () => {
    clearAuthData();
    window.location.reload();
  };

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  const toggleRouteMonitor = () => {
    setShowRouteMonitor(!showRouteMonitor);
  };

  const toggleApiStatus = () => {
    setShowApiStatus(!showApiStatus);
  };

  const getUserAccountInfo = () => {
    if (!user?.accountId) return 'N/A';

    // Verificação simplificada de conta master
    if (user.accountId === 'master-account-001') {
      return `Master Account`;
    }

    const account = mockData.getAccountById(user.accountId);
    return account ? `${account.name} (${account.id})` : 'Conta não encontrada';
  };

  const getAccountUsers = () => {
    if (!user?.accountId) return [];
    return mockData.getUsersByAccount(user.accountId);
  };

  const getDataSourceColor = (dataSource: string) => {
    switch (dataSource) {
      case 'api': return '#28a745';
      case 'mock': return '#ffc107';
      case 'unknown': return '#dc3545';
      default: return '#6c757d';
    }
  };

  const getDataSourceIcon = (dataSource: string) => {
    switch (dataSource) {
      case 'api': return '🌐';
      case 'mock': return '🎭';
      case 'unknown': return '❓';
      default: return '❓';
    }
  };

  const getApiStatusIcon = (available: boolean) => {
    return available ? '✅' : '❌';
  };

  const getApiStatusColor = (available: boolean) => {
    return available ? '#28a745' : '#dc3545';
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('pt-BR', { 
      hour: '2-digit', 
      minute: '2-digit', 
      second: '2-digit' 
    });
  };

  if (!isAuthenticated) {
    return (
      <div className="auth-debug" style={{
        position: 'fixed',
        top: '65px',
        right: '10px',
        background: '#f8f9fa',
        border: '1px solid #dee2e6',
        borderRadius: '8px',
        padding: '10px',
        fontSize: '12px',
        zIndex: 2,
        maxWidth: '300px'
      }}>
        <div
          style={{
            fontWeight: 'bold',
            marginBottom: '5px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
          onClick={toggleExpanded}
        >
          <span>🔧 Auth Debug</span>
          <span style={{ fontSize: '14px' }}>{isExpanded ? '▼' : '▶'}</span>
        </div>

        {isExpanded && (
          <>
            <div>Status: <span style={{ color: '#dc3545' }}>Não autenticado</span></div>
            <div>API: <span style={{ color: apiStatus === 'connected' ? '#28a745' : '#ffc107' }}>{apiStatus}</span></div>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="auth-debug" style={{
      position: 'fixed',
      top: '66px',
      right: '10px',
      background: '#f8f9fa',
      border: '1px solid #dee2e6',
      borderRadius: '8px',
      padding: '10px',
      fontSize: '12px',
      zIndex: 2,
      maxWidth: isExpanded ? '450px' : '200px',
      transition: 'max-width 0.3s ease'
    }}>
      <div
        style={{
          fontWeight: 'bold',
          marginBottom: isExpanded ? '8px' : '0',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
        onClick={toggleExpanded}
      >
        <span>🔧 Auth Debug</span>
        <span style={{ fontSize: '14px' }}>{isExpanded ? '▼' : '▶'}</span>
      </div>

      {isExpanded && (
        <>
          <div style={{ marginBottom: '5px' }}>
            <strong>Usuário:</strong> {user?.name}
          </div>

          <div style={{ marginBottom: '5px' }}>
            <strong>Email:</strong> {user?.email}
          </div>

          <div style={{ marginBottom: '5px' }}>
            <strong>Role:</strong>
            <span style={{
              color: user?.role === 'superadmin' ? '#dc3545' :
                     user?.role === 'admin' ? '#007bff' :
                     user?.role === 'moderator' ? '#28a745' : '#6c757d'
            }}>
              {user?.role}
            </span>
          </div>

          <div style={{ marginBottom: '5px' }}>
            <strong>Conta:</strong> {getUserAccountInfo()}
          </div>

          <div style={{ marginBottom: '5px' }}>
            <strong>Account ID:</strong> {user?.accountId || 'N/A'}
          </div>

          <div style={{ marginBottom: '5px' }}>
            <strong>Status:</strong>
            <span style={{ color: user?.isActive ? '#28a745' : '#dc3545' }}>
              {user?.isActive ? 'Ativo' : 'Inativo'}
            </span>
          </div>

          <div style={{ marginBottom: '5px' }}>
            <strong>API:</strong>
            <span style={{ color: apiStatus === 'connected' ? '#28a745' : '#ffc107' }}>
              {apiStatus}
            </span>
          </div>

          {/* API Status Section */}
          <div style={{ marginTop: '10px', borderTop: '1px solid #dee2e6', paddingTop: '8px' }}>
            <div
              style={{
                fontWeight: 'bold',
                marginBottom: '5px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
              onClick={toggleApiStatus}
            >
              <span>🔍 API Status ({apiTestResults.filter(r => r.available).length}/{apiTestResults.length})</span>
              <span style={{ fontSize: '12px' }}>{showApiStatus ? '▼' : '▶'}</span>
            </div>

            {showApiStatus && (
              <div style={{ marginTop: '5px' }}>
                {isTestingApis ? (
                  <div style={{ fontSize: '11px', color: '#6c757d', fontStyle: 'italic' }}>
                    🔄 Testando APIs...
                  </div>
                ) : apiTestResults.length === 0 ? (
                  <div style={{ fontSize: '11px', color: '#6c757d', fontStyle: 'italic' }}>
                    Nenhum teste realizado
                  </div>
                ) : (
                  <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                    {apiTestResults.map((result, index) => (
                      <div key={index} style={{
                        fontSize: '11px',
                        marginBottom: '3px',
                        padding: '2px 4px',
                        background: result.available ? '#d4edda' : '#f8d7da',
                        borderRadius: '3px',
                        border: `1px solid ${result.available ? '#c3e6cb' : '#f5c6cb'}`
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <span style={{ fontWeight: 'bold' }}>{result.name}</span>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <span style={{ color: getApiStatusColor(result.available) }}>
                              {getApiStatusIcon(result.available)}
                            </span>
                            <span style={{ color: getDataSourceColor(result.dataSource) }}>
                              {getDataSourceIcon(result.dataSource)}
                            </span>
                          </div>
                        </div>
                        <div style={{ fontSize: '10px', color: '#6c757d' }}>
                          {result.responseTime && `${result.responseTime}ms`}
                          {result.error && ` • ${result.error}`}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                <div style={{ marginTop: '5px', display: 'flex', gap: '5px' }}>
                  <button
                    onClick={testAllApis}
                    disabled={isTestingApis}
                    style={{
                      padding: '2px 6px',
                      fontSize: '10px',
                      background: isTestingApis ? '#6c757d' : '#007bff',
                      color: 'white',
                      border: 'none',
                      borderRadius: '3px',
                      cursor: isTestingApis ? 'not-allowed' : 'pointer'
                    }}
                  >
                    🔄 Retestar
                  </button>
                  <button
                    onClick={testAllApisComplete}
                    disabled={isTestingApis}
                    style={{
                      padding: '2px 6px',
                      fontSize: '10px',
                      background: isTestingApis ? '#6c757d' : '#28a745',
                      color: 'white',
                      border: 'none',
                      borderRadius: '3px',
                      cursor: isTestingApis ? 'not-allowed' : 'pointer'
                    }}
                  >
                    🔍 Todas
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Route Monitor Section */}
          <div style={{ marginTop: '10px', borderTop: '1px solid #dee2e6', paddingTop: '8px' }}>
            <div
              style={{
                fontWeight: 'bold',
                marginBottom: '5px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
              onClick={toggleRouteMonitor}
            >
              <span>📊 Route Monitor ({routeStatuses.size})</span>
              <span style={{ fontSize: '12px' }}>{showRouteMonitor ? '▼' : '▶'}</span>
            </div>

            {showRouteMonitor && (
              <div style={{ marginTop: '5px' }}>
                {Array.from(routeStatuses.values()).length === 0 ? (
                  <div style={{ fontSize: '11px', color: '#6c757d', fontStyle: 'italic' }}>
                    Nenhuma rota acessada ainda
                  </div>
                ) : (
                  <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                    {Array.from(routeStatuses.values())
                      .sort((a, b) => b.lastAccessed.getTime() - a.lastAccessed.getTime())
                      .map((status, index) => (
                        <div key={index} style={{
                          fontSize: '11px',
                          marginBottom: '3px',
                          padding: '2px 4px',
                          background: status.success ? '#d4edda' : '#f8d7da',
                          borderRadius: '3px',
                          border: `1px solid ${status.success ? '#c3e6cb' : '#f5c6cb'}`
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <span style={{ fontWeight: 'bold' }}>{status.route}</span>
                            <span style={{ color: getDataSourceColor(status.dataSource) }}>
                              {getDataSourceIcon(status.dataSource)}
                            </span>
                          </div>
                          <div style={{ fontSize: '10px', color: '#6c757d' }}>
                            {formatTime(status.lastAccessed)}
                            {status.responseTime && ` • ${status.responseTime}ms`}
                            {status.error && ` • ${status.error}`}
                          </div>
                        </div>
                      ))}
                  </div>
                )}
                
                <div style={{ marginTop: '5px', display: 'flex', gap: '5px' }}>
                  <button
                    onClick={clearRouteStatuses}
                    style={{
                      padding: '2px 6px',
                      fontSize: '10px',
                      background: '#6c757d',
                      color: 'white',
                      border: 'none',
                      borderRadius: '3px',
                      cursor: 'pointer'
                    }}
                  >
                    🗑️ Limpar
                  </button>
                </div>
              </div>
            )}
          </div>

          {user?.role === 'superadmin' && (
            <div style={{ marginTop: '8px', padding: '5px', background: '#fff3cd', borderRadius: '4px' }}>
              <div style={{ fontWeight: 'bold', marginBottom: '3px' }}>🏢 Contas Disponíveis:</div>
              {mockData.getAccountsWithUsers().map(account => (
                <div key={account.id} style={{ fontSize: '11px', marginBottom: '2px' }}>
                  • {account.name} ({account.users.length} usuários)
                </div>
              ))}
            </div>
          )}

          {user?.accountId && user.role !== 'superadmin' && (
            <div style={{ marginTop: '8px', padding: '5px', background: '#d1ecf1', borderRadius: '4px' }}>
              <div style={{ fontWeight: 'bold', marginBottom: '3px' }}>👥 Usuários da Conta:</div>
              {getAccountUsers().map(accountUser => (
                <div key={accountUser.id} style={{ fontSize: '11px', marginBottom: '2px' }}>
                  • {accountUser.name}
                </div>
              ))}
            </div>
          )}

          <button
            onClick={handleClearAuth}
            style={{
              marginTop: '8px',
              padding: '4px 8px',
              fontSize: '10px',
              background: '#dc3545',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            🗑️ Limpar Auth
          </button>
        </>
      )}
    </div>
  );
};

export default AuthDebug;